import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { getAllTutors } from '../services/firestore'
import { Loader, User, Calendar, Award, Sparkles, Heart } from 'lucide-react'
import { capitalizeName } from '../utils/formatters'
import './RetiredTutors.css'

const RetiredTutors = () => {
  const [retiredTutors, setRetiredTutors] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    loadRetiredTutors()
    document.title = 'Mural de Retiro y Honor - VTIC'
  }, [])

  const loadRetiredTutors = async () => {
    setLoading(true)
    try {
      const result = await getAllTutors()
      if (result.success) {
        // Filtrar solo los tutores que están retirados
        const retired = result.data.filter(t => t.isRetired === true)
        setRetiredTutors(retired)
      } else {
        setError(result.error)
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleTutorClick = (tutorId) => {
    navigate(`/tutor/${tutorId}`)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Fecha no especificada'
    try {
      const [year, month, day] = dateStr.split('-')
      const date = new Date(year, month - 1, day)
      return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return dateStr
    }
  }

  if (loading) {
    return (
      <div className="retired-tutors-page">
        <div className="loading-container">
          <Loader size={48} className="spinner" />
          <p>Cargando Mural de Retiro...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="retired-tutors-page">
        <div className="error-container">
          <p>Error al cargar el mural de retiro: {error}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="retired-tutors-page">
      <div className="retired-tutors-header">
        <div className="badge-honor">
          <Award size={20} className="gold-icon" />
          <span>CUADRO DE HONOR</span>
        </div>
        <h1 id="mural-title">Mural de Retiro</h1>
        <p className="subtitle">Homenaje a nuestros tutores que culminaron con distinción su voluntariado y dejaron una huella imborrable.</p>
        <div className="header-decoration">
          <span className="line"></span>
          <Sparkles size={18} className="sparkle" />
          <span className="line"></span>
        </div>
      </div>

      <div className="retired-tutors-grid">
        {retiredTutors.map(tutor => {
          const start = tutor.retiredData?.startDate ? formatDate(tutor.retiredData.startDate) : 'Inicio'
          const end = tutor.retiredData?.endDate ? formatDate(tutor.retiredData.endDate) : 'Fin'
          
          return (
            <div
              key={tutor.id}
              className="retired-tutor-card"
              onClick={() => handleTutorClick(tutor.id)}
              id={`retired-card-${tutor.id}`}
            >
              <div className="card-border-glow"></div>
              <div className="retired-tutor-avatar-wrapper">
                <div className="retired-tutor-avatar">
                  {tutor.photoURL ? (
                    <img src={tutor.photoURL} alt={capitalizeName(tutor.fullName)} />
                  ) : (
                    <div className="avatar-placeholder">
                      <User size={48} />
                    </div>
                  )}
                </div>
                <div className="honor-medal">
                  <Award size={18} />
                </div>
              </div>

              <div className="retired-tutor-info">
                <h2>{capitalizeName(tutor.fullName) || 'Tutor de Honor'}</h2>
                
                <div className="volunteering-dates">
                  <Calendar size={14} className="date-icon" />
                  <span>{start} - {end}</span>
                </div>

                {tutor.retiredData?.honorBio ? (
                  <p className="retired-bio">{tutor.retiredData.honorBio}</p>
                ) : (
                  <p className="retired-bio empty-bio">Voluntario destacado de la comunidad VTIC.</p>
                )}

                <div className="card-footer">
                  <span className="gratitude-badge">
                    <Heart size={12} className="heart-icon" /> Gracias por tu servicio
                  </span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {retiredTutors.length === 0 && (
        <div className="empty-state-retired">
          <Award size={64} className="gold-icon-large" />
          <h2>Aún no se registran retiros de honor</h2>
          <p>Los tutores que finalicen su periodo de voluntariado con distinción aparecerán en este espacio.</p>
        </div>
      )}
    </div>
  )
}

export default RetiredTutors
